import { useEffect, useState } from "react";
import {
  adminListDiscountCodes, adminCreateDiscountCode, adminUpdateDiscountCode,
  adminDeleteDiscountCode, adminListInstitutionalPayouts, adminMarkInstitutionalPaid,
} from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

const apiErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong.";
};

const SOURCE_TYPES = [
  { value: "rep", label: "Sales Rep (TT1 → Thomas etc.)" },
  { value: "institution", label: "Institution (Home Depot, Lowe's — accrues fee)" },
  { value: "other", label: "Other / Marketing" },
];

export default function AdminDiscountCodesPage() {
  const [tab, setTab] = useState("codes"); // 'codes' | 'payouts'
  const [codes, setCodes] = useState([]);
  const [payouts, setPayouts] = useState({ items: [], totals: { pending_cents: 0, paid_cents: 0, by_recipient: {} } });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, payoutData] = await Promise.all([
        adminListDiscountCodes(),
        adminListInstitutionalPayouts(),
      ]);
      setCodes(list.items || []);
      setPayouts(payoutData);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? Past subscriptions keep their discount; the code stops working for new ones.`)) return;
    setBusy(c.id);
    try {
      await adminDeleteDiscountCode(c.id);
      await load();
    } catch (e) {
      alert(apiErr(e));
    } finally { setBusy(null); }
  };

  const handleToggleActive = async (c) => {
    setBusy(c.id);
    try {
      await adminUpdateDiscountCode(c.id, { active: !c.active });
      await load();
    } catch (e) {
      alert(apiErr(e));
    } finally { setBusy(null); }
  };

  const handleMarkPaid = async (payoutIds, recipient) => {
    if (!payoutIds.length) return;
    const method = window.prompt(`How was ${recipient} paid? (e.g. "ACH 6/15", "Check #1042")`, "ACH");
    if (!method) return;
    setBusy("paying");
    try {
      const r = await adminMarkInstitutionalPaid(payoutIds, method, "");
      alert(`Marked ${r.marked_paid} payouts paid.`);
      await load();
    } catch (e) {
      alert(apiErr(e));
    } finally { setBusy(null); }
  };

  const pendingByRecipient = {};
  for (const p of payouts.items) {
    if (p.status !== "pending") continue;
    if (!pendingByRecipient[p.recipient_name]) pendingByRecipient[p.recipient_name] = [];
    pendingByRecipient[p.recipient_name].push(p);
  }

  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "32px 24px" }} data-testid="admin-discount-codes-page">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>OWNER · DISCOUNT CODES</div>
            <h1 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 44, color: NAVY, margin: "4px 0 0" }}>
              CODES + PAYOUTS
            </h1>
          </div>
          <div>
            <button onClick={() => setShowCreate(true)} style={btnPrimary} data-testid="discount-codes-add-btn">+ NEW CODE</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
          <KpiTile label="Active codes" value={codes.filter((c) => c.active).length} />
          <KpiTile label="Total redemptions" value={codes.reduce((s, c) => s + (c.total_uses || 0), 0)} />
          <KpiTile label="Owed to institutions" value={fmt(payouts.totals.pending_cents)} highlight />
          <KpiTile label="Paid to institutions" value={fmt(payouts.totals.paid_cents)} />
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 24 }}>
          <TabBtn onClick={() => setTab("codes")} active={tab === "codes"} testid="tab-codes">CODES ({codes.length})</TabBtn>
          <TabBtn onClick={() => setTab("payouts")} active={tab === "payouts"} testid="tab-payouts">
            INSTITUTIONAL PAYOUTS ({payouts.items.length})
          </TabBtn>
        </div>

        {error && <div style={errBox}>{error}</div>}

        {loading && <div style={{ padding: 24, color: "#5b5e69" }}>Loading…</div>}

        {!loading && tab === "codes" && (
          <div style={cardWrap} data-testid="codes-table-wrap">
            {codes.length === 0 ? (
              <div style={emptyBox} data-testid="codes-empty">
                No codes yet. Click <strong>+ NEW CODE</strong> to create one (e.g. <code>TT1</code> for Thomas at 10% off, or <code>HD10</code> for Home Depot 10%/10%).
              </div>
            ) : (
              <table style={tbl} data-testid="codes-table">
                <thead>
                  <tr>
                    <th style={th}>Code</th>
                    <th style={th}>Discount</th>
                    <th style={th}>Source</th>
                    <th style={th}>Inst Fee %</th>
                    <th style={th}>Uses</th>
                    <th style={th}>Expires</th>
                    <th style={th}>Status</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => (
                    <tr key={c.id} style={{ borderTop: "1px solid #e5e7ee" }}>
                      <td style={td}><code style={pillCode}>{c.code}</code></td>
                      <td style={td}><strong>{c.discount_pct}%</strong> off</td>
                      <td style={td}>
                        <div style={{ fontWeight: 700 }}>{c.source_label}</div>
                        <div style={{ fontSize: 11, color: "#5b5e69" }}>{c.source_type}{c.rep_slug ? ` · ${c.rep_slug}` : ""}</div>
                      </td>
                      <td style={td}>{c.referral_fee_pct ? (
                        <div>
                          <strong style={{ color: "#1A7A4A" }}>{c.referral_fee_pct}%</strong> → {c.referral_recipient_name}
                        </div>
                      ) : "—"}</td>
                      <td style={td}>{c.total_uses || 0}</td>
                      <td style={td} data-testid={`code-expires-${c.code}`}>
                        {c.expires_at ? (
                          <span style={{ color: c.expired ? "#A32D2D" : NAVY, fontWeight: 600 }}>
                            {fmtDate(c.expires_at)}
                          </span>
                        ) : (
                          <span style={{ color: "#5b5e69" }}>Unlimited</span>
                        )}
                      </td>
                      <td style={td}>
                        {c.expired
                          ? <span style={{ ...badgeOff, background: "#FDECEC", color: "#A32D2D" }}>EXPIRED</span>
                          : c.active
                          ? <span style={badgeActive}>ACTIVE</span>
                          : <span style={badgeOff}>OFF</span>}
                      </td>
                      <td style={td}>
                        <button onClick={() => setEditing(c)} style={btnLink} data-testid={`code-edit-${c.id}`}>EDIT</button>
                        <button onClick={() => handleToggleActive(c)} style={btnLink} disabled={busy === c.id}>
                          {c.active ? "DISABLE" : "ENABLE"}
                        </button>
                        <button onClick={() => handleDelete(c)} style={{ ...btnLink, color: "#A32D2D" }} data-testid={`code-delete-${c.id}`}>
                          DELETE
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!loading && tab === "payouts" && (
          <div style={cardWrap} data-testid="payouts-table-wrap">
            {payouts.items.length === 0 ? (
              <div style={emptyBox} data-testid="payouts-empty">
                No institutional fees accrued yet. As soon as a contractor pays through an institution code (e.g. HD10), their share will show up here.
              </div>
            ) : (
              <>
                {Object.keys(pendingByRecipient).length > 0 && (
                  <div style={{ background: "#FFF8E5", borderBottom: "1px solid #e5e7ee", padding: 16 }}>
                    <strong style={{ color: NAVY }}>QUICK ACTIONS · MARK PENDING PAID</strong>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      {Object.entries(pendingByRecipient).map(([rec, items]) => (
                        <button key={rec} style={btnSecondary}
                                onClick={() => handleMarkPaid(items.map((i) => i.id), rec)}
                                data-testid={`mark-paid-${rec.replace(/\s+/g, "-")}`}>
                          MARK {items.length} PENDING PAID → {rec}{" "}
                          ({fmt(items.reduce((s, i) => s + i.fee_cents, 0))})
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <table style={tbl} data-testid="payouts-table">
                  <thead>
                    <tr>
                      <th style={th}>Recipient</th>
                      <th style={th}>Code</th>
                      <th style={th}>Contractor</th>
                      <th style={th}>Invoice</th>
                      <th style={th}>Fee</th>
                      <th style={th}>Status</th>
                      <th style={th}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.items.map((p) => (
                      <tr key={p.id} style={{ borderTop: "1px solid #e5e7ee" }}>
                        <td style={td}>{p.recipient_name}</td>
                        <td style={td}><code style={pillCode}>{p.discount_code}</code></td>
                        <td style={td}>
                          <div>{p.contractor_business || "—"}</div>
                          <div style={{ fontSize: 11, color: "#5b5e69" }}>{p.contractor_email}</div>
                        </td>
                        <td style={td}>
                          <div>{fmt(p.invoice_amount_cents)}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{p.invoice_id}</div>
                        </td>
                        <td style={td}>
                          <strong>{fmt(p.fee_cents)}</strong>{" "}
                          <span style={{ color: "#5b5e69", fontSize: 11 }}>({p.referral_fee_pct}%)</span>
                        </td>
                        <td style={td}>
                          {p.status === "paid"
                            ? <span style={badgeActive}>PAID</span>
                            : <span style={{ ...badgeOff, background: "#FFF1C7", color: "#9C7800" }}>PENDING</span>}
                        </td>
                        <td style={td}>{fmtDate(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
      </div>

      {showCreate && (
        <CodeEditorModal
          mode="create"
          onClose={() => setShowCreate(false)}
          onSave={async (payload) => {
            try {
              await adminCreateDiscountCode(payload);
              setShowCreate(false);
              await load();
            } catch (e) { alert(apiErr(e)); }
          }}
        />
      )}
      {editing && (
        <CodeEditorModal
          mode="edit"
          existing={editing}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            try {
              await adminUpdateDiscountCode(editing.id, payload);
              setEditing(null);
              await load();
            } catch (e) { alert(apiErr(e)); }
          }}
        />
      )}
    </div>
  );
}

function CodeEditorModal({ mode, existing, onClose, onSave }) {
  const [code, setCode] = useState(existing?.code || "");
  const [pct, setPct] = useState(existing?.discount_pct ?? 10);
  const [sourceLabel, setSourceLabel] = useState(existing?.source_label || "");
  const [sourceType, setSourceType] = useState(existing?.source_type || "rep");
  const [repSlug, setRepSlug] = useState(existing?.rep_slug || "");
  const [hasFee, setHasFee] = useState(!!existing?.referral_fee_pct);
  const [feePct, setFeePct] = useState(existing?.referral_fee_pct ?? 10);
  const [recipName, setRecipName] = useState(existing?.referral_recipient_name || "");
  const [recipEmail, setRecipEmail] = useState(existing?.referral_recipient_email || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [hasExpiration, setHasExpiration] = useState(!!existing?.expires_at);
  const [expiresAt, setExpiresAt] = useState(
    existing?.expires_at ? String(existing.expires_at).slice(0, 10) : ""
  );

  const onSubmit = (e) => {
    e.preventDefault();
    const payload = {
      discount_pct: Number(pct),
      source_label: sourceLabel,
      referral_fee_pct: hasFee ? Number(feePct) : null,
      referral_recipient_name: hasFee ? recipName : null,
      referral_recipient_email: hasFee ? recipEmail : null,
      notes,
    };
    if (mode === "create") {
      payload.code = code.trim().toUpperCase();
      payload.source_type = sourceType;
      payload.rep_slug = sourceType === "rep" ? (repSlug || null) : null;
      if (hasExpiration && expiresAt) {
        payload.expires_at = expiresAt;
      }
    } else {
      // Edit mode — explicitly send expiration changes.
      if (hasExpiration && expiresAt) {
        payload.expires_at = expiresAt;
        payload.clear_expiration = false;
      } else if (!hasExpiration && existing?.expires_at) {
        payload.clear_expiration = true;
      }
    }
    onSave(payload);
  };

  return (
    <div style={scrim} onClick={onClose} data-testid="code-editor-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} style={modalCard}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>
          {mode === "create" ? "NEW DISCOUNT CODE" : `EDIT · ${existing?.code}`}
        </div>
        <h2 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, color: NAVY, fontSize: 30, margin: "4px 0 18px" }}>
          {mode === "create" ? "CREATE A CODE" : "UPDATE CODE"}
        </h2>

        {mode === "create" && (
          <Field label="CODE (e.g. TT1 — A-Z, 0-9, dashes only)">
            <input required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} style={input} data-testid="code-input" placeholder="TT1" />
          </Field>
        )}

        <Field label="% DISCOUNT (1–100)">
          <input required type="number" min={1} max={100} value={pct} onChange={(e) => setPct(e.target.value)} style={input} data-testid="discount-pct-input" />
        </Field>

        <Field label="SOURCE LABEL (shown on dashboards)">
          <input required value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)} style={input} data-testid="source-label-input" placeholder="Thomas Thomaidis" />
        </Field>

        {mode === "create" && (
          <Field label="SOURCE TYPE">
            <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} style={input} data-testid="source-type-select">
              {SOURCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        )}

        {mode === "create" && sourceType === "rep" && (
          <Field label="REP SLUG (matches the slug used in ?rep= signup links)">
            <input value={repSlug} onChange={(e) => setRepSlug(e.target.value)} style={input} placeholder="thomas" data-testid="rep-slug-input" />
          </Field>
        )}

        <div style={{ border: "1px dashed #d0d3da", padding: 14, borderRadius: 12, marginTop: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <input type="checkbox" checked={hasFee} onChange={(e) => setHasFee(e.target.checked)} style={{ width: 18, height: 18, accentColor: NAVY }} data-testid="has-fee-checkbox" />
            <strong>This code OWES a referral fee</strong> (institution / aggregator)
          </label>
          {hasFee && (
            <>
              <Field label="REFERRAL FEE % (paid to recipient on every paid invoice)">
                <input type="number" min={0} max={100} value={feePct} onChange={(e) => setFeePct(e.target.value)} style={input} data-testid="fee-pct-input" />
              </Field>
              <Field label="RECIPIENT NAME">
                <input value={recipName} onChange={(e) => setRecipName(e.target.value)} style={input} placeholder="Home Depot Pro Marketing" data-testid="recipient-name-input" />
              </Field>
              <Field label="RECIPIENT EMAIL">
                <input type="email" value={recipEmail} onChange={(e) => setRecipEmail(e.target.value)} style={input} placeholder="pro@homedepot.com" data-testid="recipient-email-input" />
              </Field>
            </>
          )}
        </div>

        <div style={{ border: "1px dashed #d0d3da", padding: 14, borderRadius: 12, marginTop: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <input type="checkbox" checked={hasExpiration} onChange={(e) => setHasExpiration(e.target.checked)} style={{ width: 18, height: 18, accentColor: NAVY }} data-testid="has-expiration-checkbox" />
            <strong>This code EXPIRES on a specific date</strong> (leave unchecked for unlimited)
          </label>
          {hasExpiration && (
            <Field label="EXPIRES AT (end of day UTC on the selected date)">
              <input
                type="date"
                value={expiresAt}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={input}
                data-testid="expires-at-input"
                required
              />
            </Field>
          )}
        </div>

        <Field label="INTERNAL NOTES">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...input, minHeight: 60, resize: "vertical" }} data-testid="notes-input" />
        </Field>

        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button type="submit" style={btnPrimary} data-testid="code-save-button">
            {mode === "create" ? "CREATE CODE" : "SAVE CHANGES"}
          </button>
          <button type="button" onClick={onClose} style={btnGhost}>Cancel</button>
        </div>

        {mode === "create" && (
          <p style={{ color: "#5b5e69", fontSize: 12, marginTop: 14 }}>
            We&apos;ll create the matching Stripe Coupon + Promotion Code automatically. Past subscriptions never lose their discount.
          </p>
        )}
      </form>
    </div>
  );
}

function KpiTile({ label, value, highlight }) {
  return (
    <div style={{ flex: 1, minWidth: 170, background: highlight ? NAVY : "#fff", color: highlight ? GOLD : NAVY, border: "1px solid #e5e7ee", borderRadius: 14, padding: 16 }}>
      <div style={{ color: highlight ? GOLD : "#5b5e69", letterSpacing: 2, fontSize: 11, fontWeight: 700 }}>{label.toUpperCase()}</div>
      <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 38, letterSpacing: 1, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function TabBtn({ children, onClick, active, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            style={{ background: active ? NAVY : "#fff", color: active ? GOLD : NAVY,
                     border: `1px solid ${active ? NAVY : "#e5e7ee"}`, padding: "10px 18px",
                     borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, letterSpacing: 1.5 }}>
      {children}
    </button>
  );
}

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ color: NAVY, letterSpacing: 2, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);

const cardWrap = { background: "#fff", borderRadius: 14, border: "1px solid #e5e7ee", marginTop: 12, overflow: "auto" };
const tbl = { width: "100%", borderCollapse: "collapse", minWidth: 720 };
const th = { textAlign: "left", padding: "12px 14px", color: NAVY, fontSize: 12, letterSpacing: 1.5, fontWeight: 700, background: BG, whiteSpace: "nowrap" };
const td = { padding: "12px 14px", color: NAVY, fontSize: 13, verticalAlign: "top" };
const input = { width: "100%", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box" };
const btnPrimary = { background: GOLD, color: NAVY, border: "none", padding: "12px 22px", borderRadius: 10, fontWeight: 700, letterSpacing: 2, cursor: "pointer" };
const btnSecondary = { background: "#fff", color: NAVY, border: `1px solid ${NAVY}`, padding: "10px 16px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, cursor: "pointer", fontSize: 12 };
const btnGhost = { background: "transparent", color: NAVY, border: `1px solid ${NAVY}`, padding: "12px 22px", borderRadius: 10, fontWeight: 700, letterSpacing: 2, cursor: "pointer" };
const btnLink = { background: "transparent", color: NAVY, border: "none", padding: "4px 8px", cursor: "pointer", fontWeight: 700, fontSize: 12, letterSpacing: 1 };
const pillCode = { background: NAVY, color: GOLD, padding: "4px 10px", borderRadius: 6, fontFamily: "monospace", fontSize: 13, fontWeight: 700, letterSpacing: 1 };
const badgeActive = { background: "#1A7A4A", color: "#fff", padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 1.5 };
const badgeOff = { background: "#e5e7ee", color: NAVY, padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 1.5 };
const emptyBox = { padding: 32, color: "#5b5e69" };
const scrim = { position: "fixed", inset: 0, background: "rgba(10,22,40,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", zIndex: 1000, overflowY: "auto" };
const modalCard = { background: "#fff", borderRadius: 18, padding: 28, maxWidth: 600, width: "100%" };
const errBox = { background: "#FDECEC", color: "#A32D2D", padding: "12px 16px", borderRadius: 10, marginTop: 16 };
